import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { 
  DollarSign, 
  CreditCard,
  RefreshCw,
  AlertTriangle,
  Download,
  TrendingUp,
  Calendar,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";

const AdminBilling = () => {
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [refundData, setRefundData] = useState({ amount: "", reason: "" });
  const [processing, setProcessing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [typeFilter, statusFilter]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("transaction_type", typeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);

      const [overviewRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/billing/overview`, { headers }),
        axios.get(`${API}/admin/billing/transactions?${params.toString()}`, { headers })
      ]);

      setOverview(overviewRes.data);
      setTransactions(transactionsRes.data.transactions);
    } catch (error) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("transaction_type", typeFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const response = await axios.get(
        `${API}/admin/billing/transactions/export?${params.toString()}`,
        { 
          headers,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transactions_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      toast.error("Failed to export transactions");
    } finally {
      setExporting(false);
    }
  };

  const handleRefund = async () => {
    if (!selectedTransaction || !refundData.reason) {
      toast.error("Please provide a reason for the refund");
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem("admin_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API}/admin/billing/refund`,
        {
          transaction_id: selectedTransaction.transaction_id,
          amount: refundData.amount ? parseFloat(refundData.amount) : null,
          reason: refundData.reason
        },
        { headers }
      );

      toast.success("Refund processed successfully");
      setRefundDialogOpen(false);
      setRefundData({ amount: "", reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Refund failed");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { label: "Pending", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
      completed: { label: "Completed", className: "bg-green-500/20 text-green-400 border-green-500/30" },
      failed: { label: "Failed", className: "bg-red-500/20 text-red-400 border-red-500/30" },
      refunded: { label: "Refunded", className: "bg-purple-500/20 text-purple-400 border-purple-500/30" }
    };
    return badges[status] || badges.pending;
  };

  const getTypeBadge = (type) => {
    const badges = {
      subscription: { label: "Subscription", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
      one_time: { label: "One-time", className: "bg-green-500/20 text-green-400 border-green-500/30" },
      purchase: { label: "Purchase", className: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
      deposit: { label: "Deposit", className: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
      final_payment: { label: "Final Payment", className: "bg-teal-500/20 text-teal-400 border-teal-500/30" }
    };
    return badges[type] || { label: type?.replace("_", " "), className: "bg-gray-500/20 text-gray-400 border-gray-500/30" };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentMonth = overview?.current_month;

  return (
    <div className="space-y-8" data-testid="admin-billing">
      <div>
        <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Billing & Revenue</h1>
        <p className="text-muted-foreground">Monitor revenue and manage transactions</p>
      </div>

      {/* Revenue Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-green-400/10 rounded-sm">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-1">${overview?.mrr?.toFixed(2) || "0.00"}</h3>
            <p className="text-sm text-muted-foreground">Monthly Recurring Revenue</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-400/10 rounded-sm">
                <CreditCard className="w-5 h-5 text-blue-400" />
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-1">${overview?.total_purchase_revenue?.toFixed(2) || "0.00"}</h3>
            <p className="text-sm text-muted-foreground">Total Purchase Revenue</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-400/10 rounded-sm">
                <RefreshCw className="w-5 h-5 text-purple-400" />
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-1">{overview?.subscriptions?.active || 0}</h3>
            <p className="text-sm text-muted-foreground">Active Subscriptions</p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-400/10 rounded-sm">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
            </div>
            <h3 className="text-3xl font-serif mb-1">{overview?.subscriptions?.churn_rate?.toFixed(1) || 0}%</h3>
            <p className="text-sm text-muted-foreground">Churn Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Current Month Summary */}
      {currentMonth && (
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-xl font-serif flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {currentMonth.month} Transactions
            </CardTitle>
            <CardDescription>Transaction totals for the current month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-muted/30 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-sm font-medium">All Transactions</span>
                </div>
                <p className="text-2xl font-serif">${currentMonth.totals?.all?.completed_total?.toFixed(2) || "0.00"}</p>
                <p className="text-sm text-muted-foreground">{currentMonth.totals?.all?.completed_count || 0} completed</p>
              </div>
              
              <div className="p-4 bg-muted/30 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <RefreshCw className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium">Subscriptions</span>
                </div>
                <p className="text-2xl font-serif">${currentMonth.totals?.subscription?.completed_total?.toFixed(2) || "0.00"}</p>
                <p className="text-sm text-muted-foreground">{currentMonth.totals?.subscription?.completed_count || 0} payments</p>
              </div>
              
              <div className="p-4 bg-muted/30 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium">One-time</span>
                </div>
                <p className="text-2xl font-serif">${currentMonth.totals?.one_time?.completed_total?.toFixed(2) || "0.00"}</p>
                <p className="text-sm text-muted-foreground">{currentMonth.totals?.one_time?.completed_count || 0} payments</p>
              </div>
              
              <div className="p-4 bg-muted/30 rounded-sm">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-orange-400" />
                  <span className="text-sm font-medium">Purchases</span>
                </div>
                <p className="text-2xl font-serif">${currentMonth.totals?.purchase?.completed_total?.toFixed(2) || "0.00"}</p>
                <p className="text-sm text-muted-foreground">{currentMonth.totals?.purchase?.completed_count || 0} purchases</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Breakdown */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif">Revenue by Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {Object.entries(overview?.plan_breakdown || {}).map(([key, data]) => (
              <div key={key} className="p-4 bg-muted/30 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="capitalize font-medium">{key}</span>
                  <Badge variant="outline">{data.count}</Badge>
                </div>
                <p className="text-2xl font-serif">${data.revenue.toFixed(2)}</p>
                <p className="text-sm text-muted-foreground">per month</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
          <div>
            <CardTitle className="text-xl font-serif">Transactions</CardTitle>
            <CardDescription>{transactions.length} transactions found</CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="subscription">Subscription</SelectItem>
                <SelectItem value="one_time">One-time</SelectItem>
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="deposit">Deposit</SelectItem>
                <SelectItem value="final_payment">Final Payment</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-transactions-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export XLS
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Transaction ID</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Type</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Customer</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Description</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Amount</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn, index) => {
                  const status = getStatusBadge(txn.status);
                  const typeBadge = getTypeBadge(txn.transaction_type);
                  return (
                    <motion.tr
                      key={txn.transaction_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className="border-b border-border last:border-0 hover:bg-muted/30"
                    >
                      <td className="p-4">
                        <code className="text-xs bg-muted/50 px-2 py-1 rounded">
                          {txn.transaction_id}
                        </code>
                      </td>
                      <td className="p-4">
                        <Badge className={`${typeBadge.className} border`}>
                          {typeBadge.label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{txn.customer_name || "-"}</p>
                          <p className="text-sm text-muted-foreground">{txn.customer_email || "-"}</p>
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground text-sm max-w-xs truncate">
                        {txn.description || "-"}
                      </td>
                      <td className="p-4 font-medium">${txn.amount?.toFixed(2)}</td>
                      <td className="p-4">
                        <Badge className={`${status.className} border`}>
                          {status.label}
                        </Badge>
                      </td>
                      <td className="p-4 text-muted-foreground text-sm">
                        <div>
                          <p>{new Date(txn.created_at).toLocaleDateString()}</p>
                          <p className="text-xs">{new Date(txn.created_at).toLocaleTimeString()}</p>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {txn.invoice_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(txn.invoice_url, '_blank')}
                              data-testid={`view-invoice-${txn.transaction_id}`}
                            >
                              View
                            </Button>
                          )}
                          {txn.status === "completed" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => {
                                setSelectedTransaction(txn);
                                setRefundDialogOpen(true);
                              }}
                              data-testid={`refund-${txn.transaction_id}`}
                            >
                              Refund
                            </Button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {transactions.length === 0 && (
            <div className="py-12 text-center text-muted-foreground">
              No transactions found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refund Dialog */}
      <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Process Refund</DialogTitle>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-sm">
                <p className="text-sm text-muted-foreground">Transaction</p>
                <code className="text-sm">{selectedTransaction.transaction_id}</code>
                <p className="text-lg font-medium mt-2">
                  ${selectedTransaction.amount?.toFixed(2)} {selectedTransaction.currency?.toUpperCase() || "USD"}
                </p>
              </div>

              <div>
                <Label>Refund Amount (leave empty for full refund)</Label>
                <Input
                  type="number"
                  step="0.01"
                  max={selectedTransaction.amount}
                  value={refundData.amount}
                  onChange={(e) => setRefundData(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder={`Max: $${selectedTransaction.amount?.toFixed(2)}`}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Reason *</Label>
                <Textarea
                  value={refundData.reason}
                  onChange={(e) => setRefundData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Reason for refund..."
                  required
                  className="mt-2"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRefund} 
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Process Refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBilling;
